// src/components/CarrierDetail.js
// One carrier — /carriers-instructions/:carrierId. Views (?tab=):
// - contact: how the agent reaches it (mode, addresses, subject), its
//   instructions, and the advanced form / routing settings; « Modifier »
//   edits them in one form (PUT /carriers/{id}, same payload as before).
// - portal: the portal accounts (secrets never shown back: « laissez vide
//   pour garder ») and the login-code (OTP) mailbox — Gmail app password,
//   Outlook, or an account already connected.
// - claim: the claim identity (contact + bank, IBAN masked), when the
//   carrier's descriptor uses one.
// - enseignes: the portal's enseignes ↔ clients, when its descriptor scopes
//   accounts by enseigne.
// - recipes: the portal recipes (CarrierRecipes), on the same descriptor
//   rule as « Accès au portail », or when GET /carrier-recipes?carrier= already
//   lists recipes for this carrier (they stay reachable).
// - delays: the carrier's usual delay to a definitive answer, all motifs and
//   per motif (CarrierAnswerDelays, GET /carriers/{id}/answer-delays), when the
//   account has cases with this carrier; a KPI tile opens it.
// Which cards exist comes from the descriptor registry (carriersModel.js),
// never from a test on the carrier's name. ?otp_status= is the Outlook OAuth
// return (backend redirect) — read once, then removed.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChartColumn, ChevronLeft, ChevronRight, CircleCheck, Eye, EyeOff, FileText, FlaskConical, Globe, KeyRound, Landmark,
  Mail, Pencil, Plus, RefreshCw, ShieldCheck, Store, Timer, Trash2, TriangleAlert, Truck, Undo2, Unplug, Users, X,
} from 'lucide-react';
import { SiGmail, SiMicrosoftoutlook } from 'react-icons/si';
import { formatShortDate, formatShortDateTime } from '../utils/dates';
import {
  Badge, Banner, Card, ConfirmDialog, DescriptionList, EmptyState, Field, IconChip, Kpi, Modal, OverflowMenu, PageHeader,
  Switch, Tabs, Toast,
} from '../design/ui';
import CarrierRecipes from './CarrierRecipes';
import CarrierAnswerDelays, { formatBusinessDays, hasDelayRows, useCarrierAnswerDelays } from './CarrierAnswerDelays';
import { CarrierLogo, ModeBadge } from './Carriers';
import {
  activityFor, carrierAttention, contactMode, descriptorFor, emailAddress, emailPatterns, formatCount, formatShare,
  messageLimit, needsOtpMailbox, needsPortalAccount, offersPortalAccess, settingsPageFor, usesClaimIdentity, usesEnseignes, windowStart,
} from './carriersModel';
import './CarriersInstructions.css';
import './CarrierDetail.css';

// Destination scope of a credential account: null = any shipment,
// { countries: ['FR'], invert } = only these destinations (or all EXCEPT them).
const scopeToForm = (scope) => {
  if (!scope || !scope.countries || scope.countries.length === 0) {
    return { scope_preset: 'any', scope_countries: '', scope_invert: false };
  }
  const isFrOnly = scope.countries.length === 1 && scope.countries[0] === 'FR';
  if (isFrOnly && !scope.invert) return { scope_preset: 'fr', scope_countries: '', scope_invert: false };
  if (isFrOnly && scope.invert) return { scope_preset: 'intl', scope_countries: '', scope_invert: false };
  return { scope_preset: 'custom', scope_countries: scope.countries.join(', '), scope_invert: !!scope.invert };
};

const formToScope = (form) => {
  if (form.scope_preset === 'fr') return { countries: ['FR'], invert: false };
  if (form.scope_preset === 'intl') return { countries: ['FR'], invert: true };
  if (form.scope_preset === 'custom') {
    const countries = (form.scope_countries || '')
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    return countries.length ? { countries, invert: !!form.scope_invert } : null;
  }
  return null;
};

// Backend timestamps are naive UTC strings (KI-110): shared parser.
// The shared short format (utils/dates): « 28 oct., 14:05 », « 28 oct. ».
const formatDateTime = (value) => formatShortDateTime(value);
const formatDay = (value) => formatShortDate(value);

// Subject of the e-mails sent to the carrier (backend
// app/services/carrier_email_subject.py renders the real one; this preview
// fills the variables with an example).
export const DEFAULT_CARRIER_SUBJECT = '{transporteur} – Colis {suivi} – {objet}';
const SUBJECT_EXAMPLE = {
  transporteur: null, carrier: null, suivi: '878301508190', tracking: '878301508190',
  objet: 'Demande de localisation du colis', subject: 'Demande de localisation du colis',
  commande: '1183266', order: '1183266',
};

export const previewCarrierSubject = (template, carrierName) => {
  const chosen = (template || '').trim() || DEFAULT_CARRIER_SUBJECT;
  return chosen.replace(/\{([^{}]*)\}/g, (match, name) => {
    const key = name.trim().toLowerCase();
    if (!(key in SUBJECT_EXAMPLE)) return match;
    return SUBJECT_EXAMPLE[key] === null ? (carrierName || 'FedEx') : SUBJECT_EXAMPLE[key];
  });
};

const TABS = ['contact', 'portal', 'claim', 'enseignes', 'recipes', 'delays'];
const emptyCredential = {
  username: '', password: '', label: '', client_ids: [], additional_fields: {},
  scope_preset: 'any', scope_countries: '', scope_invert: false,
};
const emptyOtpForm = { email: '', password: '', imap_server: 'imap.gmail.com', imap_port: 993 };
// Claim identity (claim-form carriers): contact + bank pre-filled on the
// carrier claim form. Bank is stored encrypted server-side; GET returns it
// masked (iban_last4), so editing requires re-entering the IBAN.
const emptyClaimForm = {
  contact_as: 'sender', first_name: '', last_name: '', email: '', phone_number: '', account_number: '',
  company_name: '', address_line1: '', address_line2: '', postal_code: '', city: '',
  iban: '', bic: '', bank_name: '', account_holder_name: '',
};

const CarrierDetail = () => {
  const { carrierId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || 'fr';
  const tp = (key, one, other, options) => t(key, options.count === 1 ? one : other, options);
  const [searchParams, setSearchParams] = useSearchParams();

  const authHeaders = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    if (!authHeaders) navigate('/');
  }, [authHeaders, navigate]);

  // ------------------------------------------------------------ data
  const [carrier, setCarrier] = useState(null);
  const [carrierLoading, setCarrierLoading] = useState(true);
  const [carrierError, setCarrierError] = useState(null);
  const [descriptors, setDescriptors] = useState(null); // null = loading, [] = unavailable
  const [stats, setStats] = useState(undefined); // undefined = loading, null = unavailable
  const [credentials, setCredentials] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [ednClients, setEdnClients] = useState([]);
  const [otpMailbox, setOtpMailbox] = useState(null);
  const [otpMailboxLoading, setOtpMailboxLoading] = useState(true);
  const [otpLinkableAccounts, setOtpLinkableAccounts] = useState([]);
  const [toast, setToast] = useState(null);
  const [confirmation, setConfirmation] = useState(null); // { title, message, confirmLabel, run }
  const [confirming, setConfirming] = useState(false);

  const fetchCarrier = useCallback(async () => {
    if (!authHeaders) return;
    setCarrierLoading(true);
    setCarrierError(null);
    try {
      const response = await axios.get('/api/logistics/carriers', { headers: authHeaders });
      const found = (response.data || []).find((c) => c.id === carrierId);
      if (found) setCarrier(found);
      else setCarrierError(t('carrierDetail.notFound', 'Transporteur introuvable'));
    } catch (err) {
      setCarrierError(err.response?.data?.detail || err.message || t('carriersUi.detail.loadError', 'Impossible de charger le transporteur.'));
    } finally {
      setCarrierLoading(false);
    }
  }, [authHeaders, carrierId, t]);

  const fetchCredentials = useCallback(async () => {
    if (!authHeaders) return;
    setCredentialsLoading(true);
    try {
      const response = await axios.get(`/api/logistics/carrier-credentials/${carrierId}/all`, { headers: authHeaders });
      setCredentials(Array.isArray(response.data) ? response.data : []);
    } catch {
      setCredentials([]);
    } finally {
      setCredentialsLoading(false);
    }
  }, [authHeaders, carrierId]);

  const fetchEdnClients = useCallback(async () => {
    if (!authHeaders) return;
    try {
      const response = await axios.get('/api/logistics/clients-edn', { headers: authHeaders });
      setEdnClients(Array.isArray(response.data) ? response.data : []);
    } catch {
      setEdnClients([]);
    }
  }, [authHeaders]);

  const fetchOtpMailbox = useCallback(async () => {
    if (!authHeaders) return;
    setOtpMailboxLoading(true);
    try {
      const response = await axios.get(`/api/logistics/carriers/${carrierId}/otp-mailbox`, { headers: authHeaders });
      setOtpMailbox(response.data);
    } catch {
      setOtpMailbox(null);
    } finally {
      setOtpMailboxLoading(false);
    }
    // Accounts already connected for ticketing that can double as the OTP
    // mailbox (no re-authentication needed).
    try {
      const linkable = await axios.get(`/api/logistics/carriers/${carrierId}/otp-mailbox/linkable-accounts`, { headers: authHeaders });
      setOtpLinkableAccounts(linkable.data?.accounts || []);
    } catch {
      setOtpLinkableAccounts([]);
    }
  }, [authHeaders, carrierId]);

  const fetchDescriptors = useCallback(async () => {
    if (!authHeaders) return;
    try {
      const response = await axios.get('/api/logistics/carrier-descriptors', { headers: authHeaders });
      setDescriptors(Array.isArray(response.data?.descriptors) ? response.data.descriptors : []);
    } catch {
      setDescriptors([]);
    }
  }, [authHeaders]);

  const fetchStats = useCallback(async () => {
    if (!authHeaders) return;
    try {
      const response = await axios.get(`/api/statistics/carriers?start_date=${windowStart(30)}&limit=100`, { headers: authHeaders });
      setStats(response.data && Array.isArray(response.data.carriers) ? response.data : null);
    } catch {
      setStats(null);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchCarrier();
    fetchCredentials();
    fetchEdnClients();
    fetchOtpMailbox();
    fetchDescriptors();
    fetchStats();
  }, [fetchCarrier, fetchCredentials, fetchEdnClients, fetchOtpMailbox, fetchDescriptors, fetchStats]);

  // The carrier's usual definitive-answer delays (Kevin 05/11): a KPI tile
  // and the « Délais de réponse » view, only when the account has cases.
  const [answerDelays, reloadAnswerDelays] = useCarrierAnswerDelays(carrierId);

  const handleRefresh = () => {
    fetchCarrier();
    fetchCredentials();
    fetchOtpMailbox();
    fetchStats();
    reloadAnswerDelays();
  };

  const descriptor = useMemo(() => (carrier ? descriptorFor(carrier.name, descriptors || []) : null), [carrier, descriptors]);
  const activity = carrier ? activityFor(carrier, descriptor, stats?.carriers) : null;
  const mode = carrier ? contactMode(carrier, descriptor) : 'portal';
  const showEnseignes = carrier ? usesEnseignes(carrier, descriptor) : false;
  const showClaim = carrier ? usesClaimIdentity(carrier, descriptor) : false;
  const needsOtp = carrier ? needsOtpMailbox(carrier, descriptor) : false;
  const needsAccount = carrier ? needsPortalAccount(carrier, descriptor) : null;
  const limit = messageLimit(descriptor);
  const extraPage = settingsPageFor(descriptor);

  // « Accès au portail » and « Recettes de portail » only when the descriptor
  // offers a portal login (null while the registry loads; registry
  // unavailable ([]): offered), or when the account already has something
  // there so it stays reachable: an account, a connected code mailbox,
  // recipes for this carrier.
  const portalOffered = descriptors === null ? null : (descriptors.length === 0 || offersPortalAccess(descriptor));
  const showPortal = portalOffered === true || credentials.length > 0 || Boolean(otpMailbox?.configured);

  // Recipes listed for this carrier (the login step excluded, as the tab
  // shows them) — read only when the descriptor offers no portal.
  const [recipeCount, setRecipeCount] = useState(null);
  const carrierName = carrier?.name;
  useEffect(() => {
    setRecipeCount(null);
    if (!authHeaders || !carrierName || portalOffered !== false) return undefined;
    let alive = true;
    axios.get(`/api/logistics/carrier-recipes?carrier=${encodeURIComponent(carrierName)}`, { headers: authHeaders })
      .then((response) => {
        if (alive) setRecipeCount((response.data?.recipes || []).filter((recipe) => recipe.operation !== 'login').length);
      })
      .catch(() => { if (alive) setRecipeCount(0); });
    return () => { alive = false; };
  }, [authHeaders, carrierName, portalOffered]);
  const showRecipes = portalOffered === true || recipeCount > 0;

  // ------------------------------------------------------------ tabs
  const visibleTabs = TABS.filter((key) => {
    if (key === 'portal') return showPortal;
    if (key === 'recipes') return showRecipes;
    if (key === 'claim') return showClaim;
    if (key === 'enseignes') return showEnseignes;
    if (key === 'delays') return hasDelayRows(answerDelays);
    return true;
  });
  const requestedTab = searchParams.get('tab');
  const tab = visibleTabs.includes(requestedTab) ? requestedTab : 'contact';
  const setTab = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (!next || next === 'contact') params.delete('tab');
      else params.set('tab', next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Landing back from the Outlook OAuth redirect (?otp_status=success|failure).
  useEffect(() => {
    const otpStatus = searchParams.get('otp_status');
    if (!otpStatus) return;
    setToast(otpStatus === 'success'
      ? { tone: 'success', text: t('carrierDetail.otpOutlookConnected', 'Boîte Outlook connectée pour les codes OTP.') }
      : { tone: 'danger', text: t('carrierDetail.otpOutlookFailed', 'La connexion Outlook a échoué. Réessayez ou utilisez une boîte Gmail avec mot de passe d’application.') });
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('otp_status');
      params.set('tab', 'portal');
      return params;
    }, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const runConfirmation = async () => {
    if (!confirmation) return;
    setConfirming(true);
    try {
      await confirmation.run();
    } finally {
      setConfirming(false);
      setConfirmation(null);
    }
  };

  // ------------------------------------------------------------ contact (edit)
  const [isEditingCarrier, setIsEditingCarrier] = useState(false);
  const [carrierForm, setCarrierForm] = useState({});
  const [carrierSaving, setCarrierSaving] = useState(false);
  const [carrierSaveError, setCarrierSaveError] = useState(null);

  const handleStartEditCarrier = () => {
    setCarrierForm({
      name: carrier.name || '',
      emails: carrier.emails || [],
      contact_rules: carrier.contact_rules || '',
      message_instructions: carrier.message_instructions || '',
      email_subject_template: carrier.email_subject_template || '',
      automation: carrier.automation || false,
      is_mail: carrier.is_mail || false,
      cc_emails: carrier.cc_emails || [],
      default_enseigne: carrier.default_enseigne || '',
      email_routing_rules: JSON.stringify(carrier.email_routing_rules || [], null, 2),
      form_tracking_patterns: (carrier.form_tracking_patterns || []).join('\n'),
      form_tool: carrier.form_tool || '',
      web2case_url: carrier.web2case_url || '',
      // Key/value rows edited in the form; sent back as a map (empty map clears).
      form_defaults: Object.entries(carrier.form_defaults || {}).map(([key, value]) => ({ key, value })),
    });
    setCarrierSaveError(null);
    setIsEditingCarrier(true);
    setTab('contact');
  };

  const handleSaveCarrier = async () => {
    if (!authHeaders) return;
    setCarrierSaving(true);
    setCarrierSaveError(null);
    try {
      let parsedRoutingRules = null;
      try {
        const parsed = JSON.parse(carrierForm.email_routing_rules);
        if (Array.isArray(parsed) && parsed.length > 0) parsedRoutingRules = parsed;
      } catch { /* keep null */ }

      await axios.put(
        `/api/logistics/carriers/${carrierId}`,
        {
          name: carrierForm.name,
          emails: carrierForm.emails,
          contact_rules: carrierForm.contact_rules,
          // Always sent (backend ignores null): '' clears the instructions.
          message_instructions: carrierForm.message_instructions || '',
          // Always sent: '' restores the default subject.
          email_subject_template: (carrierForm.email_subject_template || '').trim(),
          automation: carrierForm.automation,
          is_mail: carrierForm.is_mail || false,
          cc_emails: carrierForm.cc_emails,
          // Always sent: '' clears the tenant-level default enseigne.
          default_enseigne: (carrierForm.default_enseigne || '').trim(),
          email_routing_rules: parsedRoutingRules,
          // Always sent: empty list/string clear the form routing on the backend.
          form_tracking_patterns: (carrierForm.form_tracking_patterns || '')
            .split('\n')
            .map((p) => p.trim())
            .filter(Boolean),
          form_tool: (carrierForm.form_tool || '').trim(),
          // Always sent: '' clears the per-tenant Web2Case URL.
          web2case_url: (carrierForm.web2case_url || '').trim(),
          // Always sent: an empty map clears the form defaults on the backend.
          form_defaults: Object.fromEntries(
            (carrierForm.form_defaults || [])
              .map((row) => [(row.key || '').trim(), (row.value || '').trim()])
              .filter(([key, value]) => key && value),
          ),
        },
        { headers: authHeaders },
      );
      await fetchCarrier();
      setIsEditingCarrier(false);
      setToast({ tone: 'success', text: t('carriersUi.detail.saved', '{{name}} est enregistré.', { name: carrierForm.name }) });
    } catch (err) {
      setCarrierSaveError(err.response?.data?.detail || err.message || t('carriersUi.saveError', 'L’enregistrement a échoué.'));
    } finally {
      setCarrierSaving(false);
    }
  };

  // An entry is an address or a rule object { address, tracking_patterns }:
  // only the address is edited, the rest of the rule is kept as is.
  const setEmailAt = (field, index, value) => setCarrierForm((f) => ({
    ...f,
    [field]: (f[field] || []).map((entry, i) => {
      if (i !== index) return entry;
      return entry && typeof entry === 'object' ? { ...entry, address: value } : value;
    }),
  }));
  const removeEmailAt = (field, index) => setCarrierForm((f) => ({ ...f, [field]: (f[field] || []).filter((_, i) => i !== index) }));
  const addEmail = (field) => setCarrierForm((f) => ({ ...f, [field]: [...(f[field] || []), ''] }));

  // ------------------------------------------------------------ portal accounts
  const [accountModal, setAccountModal] = useState(null); // { id?: string } — null closed
  const [accountForm, setAccountForm] = useState(emptyCredential);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountError, setAccountError] = useState(null);

  const openAddAccount = () => {
    setAccountForm(emptyCredential);
    setAccountError(null);
    setAccountModal({});
  };

  const openEditAccount = (cred) => {
    setAccountForm({
      username: cred.username || '',
      password: '',
      label: cred.label || '',
      client_ids: cred.client_ids || [],
      ...scopeToForm(cred.scope),
    });
    setAccountError(null);
    setAccountModal({ id: cred.id });
  };

  const handleSaveAccount = async () => {
    if (!authHeaders) return;
    setAccountSaving(true);
    setAccountError(null);
    try {
      if (accountModal.id) {
        const payload = {
          username: accountForm.username || undefined,
          label: accountForm.label || undefined,
          client_ids: accountForm.client_ids,
          scope: formToScope(accountForm),
        };
        if (accountForm.password) payload.password = accountForm.password;
        await axios.put(`/api/logistics/carrier-credentials/${accountModal.id}`, payload, { headers: authHeaders });
      } else {
        await axios.post(
          '/api/logistics/carrier-credentials',
          {
            carrier_id: carrierId,
            username: accountForm.username,
            password: accountForm.password,
            label: accountForm.label || accountForm.username,
            client_ids: accountForm.client_ids,
            additional_fields: accountForm.additional_fields || {},
            scope: formToScope(accountForm),
          },
          { headers: authHeaders },
        );
      }
      setAccountModal(null);
      setToast({ tone: 'success', text: t('carriersUi.portal.accountSaved', 'Compte portail enregistré.') });
      await fetchCredentials();
    } catch (err) {
      setAccountError(err.response?.data?.detail || err.message || t('carriersUi.saveError', 'L’enregistrement a échoué.'));
    } finally {
      setAccountSaving(false);
    }
  };

  const askDeleteAccount = (cred) => setConfirmation({
    title: t('carriersUi.portal.deleteTitle', 'Supprimer le compte « {{label}} » ?', { label: cred.label || cred.username }),
    message: t('carriersUi.portal.deleteMessage', 'L’agent ne pourra plus se connecter au portail {{carrier}} avec ces identifiants.', { carrier: carrier?.name }),
    confirmLabel: t('carriersUi.delete', 'Supprimer'),
    run: async () => {
      try {
        await axios.delete(`/api/logistics/carrier-credentials/${cred.id}`, { headers: authHeaders });
        setToast({ tone: 'success', text: t('carriersUi.portal.deleted', 'Compte supprimé.') });
        await fetchCredentials();
      } catch (err) {
        setToast({ tone: 'danger', text: err.response?.data?.detail || t('carriersUi.portal.deleteError', 'Le compte n’a pas pu être supprimé.') });
      }
    },
  });

  const toggleClient = (clientId) => setAccountForm((f) => ({
    ...f,
    client_ids: f.client_ids.includes(clientId) ? f.client_ids.filter((id) => id !== clientId) : [...f.client_ids, clientId],
  }));

  const getClientName = (clientId) => ednClients.find((c) => c.id === clientId)?.name || clientId;

  const scopeLabel = (scope) => {
    const form = scopeToForm(scope);
    if (form.scope_preset === 'fr') return t('carrierDetail.scopeFrance', 'France uniquement');
    if (form.scope_preset === 'intl') return t('carrierDetail.scopeInternational', 'International (hors France)');
    return (scope.invert ? t('carrierDetail.scopeExceptPrefix', 'Tout sauf : ') : '') + scope.countries.join(', ');
  };

  // ------------------------------------------------------------ OTP mailbox
  const [otpForm, setOtpForm] = useState(null); // null = closed
  const [showOtpPassword, setShowOtpPassword] = useState(false);
  const [otpSaving, setOtpSaving] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpOutlookLoading, setOtpOutlookLoading] = useState(false);
  const [otpLinking, setOtpLinking] = useState(null);

  const handleConnectOtpOutlook = async () => {
    if (!authHeaders) return;
    setOtpOutlookLoading(true);
    setOtpError(null);
    try {
      const response = await axios.get(`/api/logistics/carriers/${carrierId}/otp-mailbox/outlook-connect`, { headers: authHeaders });
      window.location.href = response.data.authorization_url;
    } catch (err) {
      setOtpError(err.response?.data?.detail || err.message);
      setOtpOutlookLoading(false);
    }
  };

  const handleSaveOtpImap = async () => {
    if (!authHeaders) return;
    setOtpSaving(true);
    setOtpError(null);
    try {
      const response = await axios.put(`/api/logistics/carriers/${carrierId}/otp-mailbox`, otpForm, { headers: authHeaders });
      setOtpMailbox(response.data);
      setOtpForm(null);
      setToast({ tone: 'success', text: t('carrierDetail.otpImapConnected', 'Boîte mail connectée et vérifiée pour les codes OTP.') });
    } catch (err) {
      setOtpError(err.response?.data?.detail || err.message);
    } finally {
      setOtpSaving(false);
    }
  };

  const askDisconnectOtp = () => setConfirmation({
    title: t('carriersUi.otp.disconnectTitle', 'Déconnecter {{email}} ?', { email: otpMailbox?.email }),
    message: t('carriersUi.otp.disconnectMessage', 'Les codes de connexion au portail {{carrier}} ne seront plus lus : la connexion de l’agent échouera tant qu’une autre boîte n’est pas connectée.', { carrier: carrier?.name }),
    confirmLabel: t('carriersUi.otp.disconnect', 'Déconnecter'),
    run: async () => {
      setOtpError(null);
      try {
        await axios.delete(`/api/logistics/carriers/${carrierId}/otp-mailbox`, { headers: authHeaders });
        setOtpMailbox({ configured: false, provider: null, email: null, connected_at: null });
        setToast({ tone: 'success', text: t('carriersUi.otp.disconnected', 'Boîte des codes déconnectée.') });
      } catch (err) {
        setOtpError(err.response?.data?.detail || err.message);
      }
    },
  });

  const handleLinkOtpAccount = async (account) => {
    if (!authHeaders) return;
    setOtpLinking(`${account.provider}:${account.email}`);
    setOtpError(null);
    try {
      const response = await axios.post(
        `/api/logistics/carriers/${carrierId}/otp-mailbox/link`,
        { provider: account.provider, email: account.email },
        { headers: authHeaders },
      );
      setOtpMailbox(response.data);
      setToast({ tone: 'success', text: t('carrierDetail.otpLinked', 'Compte connecté réutilisé pour les codes OTP.') });
    } catch (err) {
      setOtpError(err.response?.data?.detail || err.message);
    } finally {
      setOtpLinking(null);
    }
  };

  const otpProviderLabel = (provider) => ({
    outlook: t('carrierDetail.otpProviderOutlook', 'Outlook'),
    linked_outlook: t('carrierDetail.otpProviderLinkedOutlook', 'Outlook (compte connecté)'),
    linked_google: t('carrierDetail.otpProviderLinkedGoogle', 'Gmail (compte connecté)'),
    linked_account_mailbox: t('carrierDetail.otpProviderAccountMailbox', 'Boîte Gmail du compte'),
  }[provider] || t('carrierDetail.otpProviderImap', 'Gmail / IMAP'));

  const isOutlook = (provider) => provider === 'outlook' || provider === 'linked_outlook';

  // ------------------------------------------------------------ claim identity
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimForm, setClaimForm] = useState(emptyClaimForm);
  const [claimSaving, setClaimSaving] = useState(false);
  const [claimError, setClaimError] = useState(null);

  const openClaimForm = () => {
    const contact = carrier?.claim_contact || {};
    const bank = carrier?.claim_bank || {};
    setClaimForm({
      ...emptyClaimForm,
      ...contact,
      // IBAN is never returned by the API: re-entered on every edit.
      iban: '',
      bic: bank.bic || '',
      bank_name: bank.bank_name || '',
      account_holder_name: bank.account_holder_name || '',
    });
    setClaimError(null);
    setShowClaimForm(true);
    setTab('claim');
  };

  const handleSaveClaim = async () => {
    if (!authHeaders) return;
    setClaimSaving(true);
    setClaimError(null);
    try {
      const contact = {
        contact_as: claimForm.contact_as || 'sender',
        first_name: claimForm.first_name.trim(),
        last_name: claimForm.last_name.trim(),
        email: claimForm.email.trim(),
        phone_number: claimForm.phone_number.trim(),
        account_number: claimForm.account_number.trim(),
        company_name: claimForm.company_name.trim(),
        address_line1: claimForm.address_line1.trim(),
        address_line2: claimForm.address_line2.trim(),
        postal_code: claimForm.postal_code.trim(),
        city: claimForm.city.trim(),
      };
      const payload = { claim_contact: contact };
      const bankTouched = ['iban', 'bic', 'bank_name', 'account_holder_name'].some((f) => (claimForm[f] || '').trim());
      if (bankTouched) {
        if (!(claimForm.iban || '').trim()) {
          setClaimError(t('carrierDetail.claimIbanRequired', 'Ressaisissez l’IBAN complet pour modifier les coordonnées bancaires (il n’est jamais réaffiché).'));
          setClaimSaving(false);
          return;
        }
        payload.claim_bank = {
          iban: claimForm.iban.trim(),
          bic: claimForm.bic.trim(),
          bank_name: claimForm.bank_name.trim(),
          account_holder_name: claimForm.account_holder_name.trim(),
        };
      }
      await axios.put(`/api/logistics/carriers/${carrierId}`, payload, { headers: authHeaders });
      setShowClaimForm(false);
      setToast({ tone: 'success', text: t('carrierDetail.claimSaved', 'Identité de réclamation enregistrée.') });
      fetchCarrier();
    } catch (err) {
      setClaimError(err.response?.data?.detail || err.message || t('carriersUi.saveError', 'L’enregistrement a échoué.'));
    } finally {
      setClaimSaving(false);
    }
  };

  const askClearClaim = () => setConfirmation({
    title: t('carriersUi.claim.clearTitle', 'Supprimer l’identité de réclamation ?'),
    message: t('carriersUi.claim.clearMessage', 'Le contact et les coordonnées bancaires ne seront plus pré-remplis sur les formulaires de réclamation {{carrier}}.', { carrier: carrier?.name }),
    confirmLabel: t('carriersUi.delete', 'Supprimer'),
    run: async () => {
      try {
        // All-empty blocks clear the stored identity server-side.
        await axios.put(
          `/api/logistics/carriers/${carrierId}`,
          { claim_contact: emptyClaimForm, claim_bank: { iban: '', bic: '', bank_name: '', account_holder_name: '' } },
          { headers: authHeaders },
        );
        setShowClaimForm(false);
        setToast({ tone: 'success', text: t('carrierDetail.claimCleared', 'Identité de réclamation supprimée.') });
        fetchCarrier();
      } catch (err) {
        setToast({ tone: 'danger', text: err.response?.data?.detail || err.message || t('carriersUi.saveError', 'L’enregistrement a échoué.') });
      }
    },
  });

  // ------------------------------------------------------------ enseignes
  const [enseigneSync, setEnseigneSync] = useState({ loading: false, error: null, data: null, applied: null });
  const [enseigneChoices, setEnseigneChoices] = useState({});

  const syncEnseignes = useCallback(async () => {
    setEnseigneSync({ loading: true, error: null, data: null, applied: null });
    try {
      const response = await axios.post(`/api/logistics/carriers/${carrierId}/mondial-relay/enseignes/sync`, {}, { headers: authHeaders });
      const data = response.data || {};
      const choices = {};
      (data.enseignes || []).forEach((row) => {
        if (row.status === 'proposed' && row.proposal) choices[row.enseigne] = row.proposal.client_id;
      });
      setEnseigneChoices(choices);
      setEnseigneSync({ loading: false, error: null, data, applied: null });
      fetchCarrier();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setEnseigneSync({
        loading: false,
        error: typeof detail === 'string' && detail ? detail : t('carrierDetail.enseignesSyncError', 'La synchronisation des enseignes a échoué.'),
        data: null,
        applied: null,
      });
    }
  }, [carrierId, authHeaders, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyEnseignes = useCallback(async () => {
    const mappings = Object.entries(enseigneChoices)
      .filter(([, clientId]) => clientId)
      .map(([enseigne, clientId]) => ({ enseigne, client_id: clientId }));
    if (mappings.length === 0) return;
    setEnseigneSync((s) => ({ ...s, loading: true, error: null }));
    try {
      const response = await axios.post(`/api/logistics/carriers/${carrierId}/mondial-relay/enseignes/apply`, { mappings }, { headers: authHeaders });
      setEnseigneSync((s) => ({ ...s, loading: false, applied: response.data }));
      fetchEdnClients();
      syncEnseignes();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setEnseigneSync((s) => ({
        ...s,
        loading: false,
        error: typeof detail === 'string' && detail ? detail : t('carrierDetail.enseignesApplyError', 'L’enregistrement des correspondances a échoué.'),
      }));
    }
  }, [carrierId, authHeaders, enseigneChoices, t]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authHeaders) return null;

  // ------------------------------------------------------------ derived
  const addresses = (carrier?.emails || []).map(emailAddress).filter(Boolean);
  const invalidAccounts = credentials.filter((cred) => cred.is_valid === false);
  const lastUsed = credentials.map((cred) => cred.last_used).filter(Boolean).sort().pop();
  const attention = carrier && !credentialsLoading && !otpMailboxLoading && descriptors !== null
    ? carrierAttention({ carrier, descriptor, credentials, otpMailbox })
    : [];
  const advancedSet = Boolean(carrier && (
    (carrier.form_tracking_patterns || []).length || carrier.web2case_url
    || Object.keys(carrier.form_defaults || {}).length || (carrier.email_routing_rules || []).length
  ));

  const modeSentence = {
    portal: t('carriersUi.detail.modePortal', 'L’agent dépose et suit ses demandes sur le portail {{name}}.', { name: carrier?.name }),
    form: t('carriersUi.detail.modeForm', 'L’agent passe par le formulaire de réclamation de {{name}}.', { name: carrier?.name }),
    api: t('carriersUi.detail.modeApi', 'L’agent dépose ses réclamations par l’API de {{name}}.', { name: carrier?.name }),
    email: t('carriersUi.detail.modeEmail', 'L’agent écrit à {{name}} par e-mail.', { name: carrier?.name }),
    email_portal: t('carriersUi.detail.modeEmailPortal', 'L’agent écrit à {{name}} par e-mail, puis relance sur son portail.', { name: carrier?.name }),
  }[mode];

  // ------------------------------------------------------------ zones
  const renderHeader = () => {
    const subtitle = carrier ? [
      modeSentence,
      carrier.automation ? t('carriersUi.detail.automationOn', 'Automatisation activée') : t('carriersUi.detail.automationOff', 'Automatisation désactivée'),
      activity && tp('carriersUi.detail.ticketsLine', '{{count}} ticket sur 30 jours', '{{count}} tickets sur 30 jours', { count: activity.count }),
    ].filter(Boolean).join(' · ') : null;
    return (
      <PageHeader
        breadcrumb={(
          <Link to="/carriers-instructions" aria-label={t('carrierDetail.backToCarriers', 'Retour aux transporteurs')}>
            <ChevronLeft className="carrier-detail-v2__crumb-icon" aria-hidden="true" />
            {t('carriersInstructions.pageTitle', 'Transporteurs')}
          </Link>
        )}
        title={carrier ? (
          <span className="carrier-detail-v2__title">
            <CarrierLogo name={carrier.name} />
            {carrier.name}
          </span>
        ) : (carrierLoading ? <span className="u-skeleton carrier-detail-v2__title-skeleton" aria-hidden="true" /> : t('carrierDetail.notFound', 'Transporteur introuvable'))}
        subtitle={subtitle}
        actions={(
          <>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={handleRefresh}
              disabled={carrierLoading || credentialsLoading}
              title={t('carrierDetail.refresh', 'Actualiser')}
              aria-label={t('carrierDetail.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            {/* A carrier's own settings page (Colissimo return labels), from one table in carriersModel. */}
            {carrier && extraPage && (
              <button type="button" className="u-btn u-btn--secondary" onClick={() => navigate(extraPage.path)}>
                <Undo2 aria-hidden="true" />
                {t(extraPage.labelKey, extraPage.label)}
              </button>
            )}
            {carrier && !isEditingCarrier && (
              <button type="button" className="u-btn u-btn--primary" onClick={handleStartEditCarrier}>
                <Pencil aria-hidden="true" />
                {t('carriersUi.detail.edit', 'Modifier')}
              </button>
            )}
          </>
        )}
      />
    );
  };

  const attentionText = (item) => ({
    invalidAccount: [
      t('carriersUi.detail.attInvalid', 'Le portail refuse le compte « {{label}} »', { label: item.account?.label || item.account?.username }),
      t('carriersUi.attention.invalidAccountHint', 'L’agent ne peut plus se connecter à son portail avec ces identifiants : mettez à jour le mot de passe.'),
      t('carriersUi.detail.attInvalidAction', 'Mettre à jour'),
    ],
    missingAccount: [
      t('carriersUi.detail.attAccount', 'Aucun compte portail'),
      t('carriersUi.attention.missingAccountHint', 'L’agent ne peut pas déposer de demande sur son portail sans identifiants.'),
      t('carriersUi.portal.add', 'Ajouter un compte'),
    ],
    missingOtp: [
      t('carriersUi.detail.attOtp', 'Le portail envoie un code de connexion par e-mail'),
      t('carriersUi.attention.missingOtpHint', 'Connectez la boîte qui reçoit ces codes, sinon la connexion au portail échoue.'),
      t('carriersUi.detail.attOtpAction', 'Connecter une boîte'),
    ],
    missingAddress: [
      t('carriersUi.detail.attAddress', 'Aucune adresse e-mail'),
      t('carriersUi.attention.missingAddressHint', 'L’agent le contacte par e-mail mais n’a aucune adresse où écrire.'),
      t('carriersUi.addAddress', 'Ajouter une adresse'),
    ],
    missingClaim: [
      t('carriersUi.detail.attClaim', 'Identité de réclamation à remplir'),
      t('carriersUi.detail.attClaimHint', 'Le formulaire de réclamation de ce transporteur demande un contact et des coordonnées bancaires.'),
      t('carrierDetail.claimConfigure', 'Configurer l’identité de réclamation'),
    ],
  }[item.kind] || ['', '', '']);

  const attentionAction = (item) => {
    if (item.kind === 'invalidAccount') return () => { setTab('portal'); openEditAccount(item.account); };
    if (item.kind === 'missingAccount') return () => { setTab('portal'); openAddAccount(); };
    if (item.kind === 'missingAddress') return handleStartEditCarrier;
    if (item.kind === 'missingClaim') return openClaimForm;
    return () => setTab(item.tab);
  };

  const renderAttention = () => {
    if (attention.length === 0) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('carriersUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="carriers-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => {
            const [title, detail, action] = attentionText(item);
            return (
              <li className="u-row" key={item.key}>
                <Badge tone={item.tone} dot>
                  {item.tone === 'danger' ? t('carriersUi.attention.blocking', 'Bloquant') : t('carriersUi.attention.watch', 'À surveiller')}
                </Badge>
                <div className="u-row__main">
                  <div className="u-row__title">{title}</div>
                  <div className="u-row__meta u-row__meta--wrap">{detail}</div>
                </div>
                <div className="u-row__side">
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={attentionAction(item)}>
                    {action}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    const services = (activity?.services || []).filter((s) => s.service_label);
    const statsCarrier = activity?.carrier || descriptor?.name || carrier.name;
    return (
      <div className="u-kpi-row">
        {stats === undefined && <span className="u-skeleton carriers-v2__kpi-skeleton" aria-hidden="true" />}
        {stats && (
          <Kpi
            icon={ChartColumn}
            tone="info"
            label={t('carriersUi.kpi.tickets', 'Tickets 30 jours')}
            value={formatCount(activity?.count || 0, lang)}
            context={services.length > 0
              ? services.slice(0, 3).map((s) => `${s.service_label} ${formatCount(s.count, lang)}`).join(' · ')
              : (activity
                ? t('carriersUi.detail.ticketsShare', '{{share}} des tickets avec transporteur', { share: formatShare(activity.percentage, lang) })
                : t('carriersUi.detail.ticketsNone', 'aucun ticket sur 30 jours'))}
            onClick={() => navigate(`/statistics?carrier=${encodeURIComponent(statsCarrier)}&days=30`)}
            title={t('carriersUi.kpi.ticketsTitle', 'Ouvrir les statistiques')}
          />
        )}
        {hasDelayRows(answerDelays) && (
          <Kpi
            icon={Timer}
            tone="info"
            label={t('carrierDelays.kpi', 'Réponse définitive')}
            value={answerDelays.overall?.shown ? formatBusinessDays(answerDelays.overall.median_business_days, t) : '—'}
            context={answerDelays.overall?.shown
              ? t('carrierDelays.kpiContext', 'délai habituel · {{count}} dossiers sur 12 mois', { count: answerDelays.overall.cases })
              : t('carrierDelays.kpiNotEnough', 'pas encore assez de dossiers')}
            active={tab === 'delays'}
            onClick={() => setTab('delays')}
          />
        )}
        {showPortal && ((!carrier.is_mail && needsAccount !== false) || credentials.length > 0) && (credentialsLoading
          ? <span className="u-skeleton carriers-v2__kpi-skeleton" aria-hidden="true" />
          : (
            <Kpi
              icon={KeyRound}
              tone={invalidAccounts.length ? 'danger' : (needsAccount && credentials.length === 0 ? 'warning' : 'success')}
              label={t('carriersUi.kpi.accounts', 'Comptes portail')}
              value={formatCount(credentials.length, lang)}
              context={invalidAccounts.length
                ? tp('carriersUi.detail.accountsRefused', '{{count}} refusé par le portail', '{{count}} refusés par le portail', { count: invalidAccounts.length })
                : (lastUsed
                  ? t('carriersUi.detail.lastUsed', 'dernier usage le {{date}}', { date: formatDay(lastUsed) })
                  : (needsAccount ? t('carriersUi.detail.accountNeeded', 'le portail en demande un') : t('carriersUi.detail.accountOptional', 'pas exigé par ce transporteur')))}
              contextTone={invalidAccounts.length ? 'danger' : (needsAccount && credentials.length === 0 ? 'warning' : undefined)}
              active={tab === 'portal'}
              onClick={() => setTab('portal')}
            />
          ))}
        {(needsOtp || otpMailbox?.configured) && (otpMailboxLoading
          ? <span className="u-skeleton carriers-v2__kpi-skeleton" aria-hidden="true" />
          : (
            <Kpi
              icon={ShieldCheck}
              tone={otpMailbox?.configured ? 'success' : 'warning'}
              label={t('carriersUi.kpi.otpOne', 'Boîte des codes')}
              value={otpMailbox?.configured ? t('carriersUi.detail.otpOn', 'Connectée') : t('carriersUi.detail.otpOff', 'À connecter')}
              context={otpMailbox?.configured ? otpMailbox.email : t('carriersUi.detail.otpNeeded', 'le portail envoie un code à chaque connexion')}
              contextTone={otpMailbox?.configured ? undefined : 'warning'}
              active={false}
              onClick={() => setTab('portal')}
            />
          ))}
        {(carrier.is_mail || addresses.length > 0) && (
          <Kpi
            icon={Mail}
            tone={addresses.length ? undefined : 'warning'}
            label={t('carriersUi.detail.kpiAddresses', 'Adresses e-mail')}
            value={formatCount(addresses.length, lang)}
            context={addresses.length
              ? addresses[0] + ((carrier.cc_emails || []).length ? ` · ${tp('carriersUi.detail.ccCount', '{{count}} en copie', '{{count}} en copie', { count: carrier.cc_emails.length })}` : '')
              : t('carriersUi.detail.noAddress', 'aucune adresse où écrire')}
            contextTone={addresses.length ? undefined : 'warning'}
            active={tab === 'contact'}
            onClick={() => setTab('contact')}
          />
        )}
      </div>
    );
  };

  const renderAddresses = (list, emptyLabel) => (list.length === 0 ? emptyLabel : (
    <ul className="carrier-detail-v2__addresses">
      {list.map((entry, index) => {
        const patterns = emailPatterns(entry);
        return (
          <li key={`${emailAddress(entry)}-${index}`}>
            <span>{emailAddress(entry)}</span>
            {patterns.length > 0 && (
              <span className="u-muted">
                {t('carriersUi.detail.addressPatterns', 'suivis {{patterns}}', { patterns: patterns.join(', ') })}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  ));

  const renderContactView = () => (
    <div className="carriers-v2__stack">
      <Card
        icon={MODE_ICON[mode] || Globe}
        title={t('carriersUi.detail.contactTitle', 'Contact')}
        description={modeSentence}
      >
        <DescriptionList items={[
          { label: t('carriersUi.detail.mode', 'Mode de contact'), value: <ModeBadge mode={mode} t={t} /> },
          {
            label: t('carriersUi.detail.automation', 'Automatisation'),
            value: carrier.automation
              ? <Badge tone="success" dot>{t('carriersUi.list.automationOn', 'Activée')}</Badge>
              : <Badge dot>{t('carriersUi.list.automationOff', 'Désactivée')}</Badge>,
          },
          {
            label: t('carriersUi.detail.addresses', 'Adresses e-mail'),
            value: renderAddresses(carrier.emails || [], carrier.is_mail
              ? <Badge tone="warning" dot>{t('carriersUi.detail.none', 'aucune')}</Badge>
              : <span className="u-muted">—</span>),
          },
          (carrier.cc_emails || []).length > 0 && {
            label: t('carriersUi.detail.cc', 'En copie'),
            value: renderAddresses(carrier.cc_emails, null),
          },
          {
            label: t('carrierDetail.emailSubject', 'Objet des e-mails'),
            value: (
              <span>
                {previewCarrierSubject(carrier.email_subject_template, carrier.name)}
                {!carrier.email_subject_template && <span className="u-muted"> ({t('carrierDetail.emailSubjectDefaultTag', 'par défaut')})</span>}
              </span>
            ),
          },
          showEnseignes && {
            label: t('carrierDetail.defaultEnseigne', 'Enseigne Connect par défaut'),
            value: carrier.default_enseigne || <span className="u-muted">—</span>,
          },
          limit && {
            label: t('carriersUi.detail.limit', 'Longueur des messages'),
            value: t('carriersUi.detail.limitValue', '{{limit}} caractères au plus', { limit }),
          },
        ]}
        />
      </Card>

      <Card
        icon={FileText}
        title={t('carriersUi.detail.instructionsTitle', 'Consignes pour l’agent')}
        description={t('carriersUi.detail.instructionsDesc', 'Ce que l’agent lit avant de contacter {{name}}, en plus des consignes générales.', { name: carrier.name })}
        footerNote={limit
          ? t('carriersUi.detail.alwaysTrackingLimit', 'Le numéro de suivi est toujours inclus, et les messages restent sous {{limit}} caractères, quelles que soient ces consignes.', { limit })
          : t('carriersUi.detail.alwaysTracking', 'Le numéro de suivi est toujours inclus dans le message, quelles que soient ces consignes.')}
      >
        <div className="carrier-detail-v2__texts">
          <div>
            <div className="u-overline">{t('carriersInstructions.contactRules', 'Instructions de contact')}</div>
            {carrier.contact_rules
              ? <p className="carrier-detail-v2__text">{carrier.contact_rules}</p>
              : <p className="u-muted carrier-detail-v2__text">{t('carriersUi.detail.noRules', 'Aucune consigne : l’agent s’appuie sur ce qu’il sait du transporteur.')}</p>}
          </div>
          <div>
            <div className="u-overline">{t('carriersUi.detail.messageInstructions', 'Consignes de message')}</div>
            {carrier.message_instructions
              ? <p className="carrier-detail-v2__text">{carrier.message_instructions}</p>
              : <p className="u-muted carrier-detail-v2__text">{t('carriersUi.detail.noMessageInstructions', 'Aucune : seules les consignes générales s’appliquent.')}</p>}
          </div>
        </div>
      </Card>

      {advancedSet && (
        <section className="u-card">
          <details className="u-disclosure">
            <summary>
              <ChevronRight aria-hidden="true" />
              {t('carriersUi.detail.advanced', 'Avancé : formulaire et routage des adresses')}
            </summary>
            <div className="carrier-detail-v2__advanced">
              <DescriptionList items={[
                (carrier.form_tracking_patterns || []).length > 0 && {
                  label: t('carrierDetail.formRouting', 'Routage formulaire'),
                  value: (
                    <span className="carrier-detail-v2__codes">
                      {carrier.form_tracking_patterns.map((pattern) => <code key={pattern} className="u-code">{pattern}</code>)}
                      <span className="u-muted">→ {carrier.form_tool}</span>
                    </span>
                  ),
                },
                carrier.web2case_url && { label: t('carrierDetail.web2caseUrl', 'URL du formulaire web transporteur (Web2Case)'), value: carrier.web2case_url },
                Object.keys(carrier.form_defaults || {}).length > 0 && {
                  label: t('carrierDetail.formDefaults', 'Valeurs par défaut du formulaire'),
                  value: (
                    <span className="carrier-detail-v2__codes">
                      {Object.entries(carrier.form_defaults).map(([key, value]) => <code key={key} className="u-code">{key} = {value}</code>)}
                    </span>
                  ),
                },
              ]}
              />
              {(carrier.email_routing_rules || []).length > 0 && (
                <div className="u-table-scroll">
                  <table className="u-table carrier-detail-v2__routing">
                    <thead>
                      <tr>
                        <th style={{ width: '30%' }}>{t('carrierDetail.matchField', 'Champ')}</th>
                        <th style={{ width: '25%' }}>{t('carrierDetail.matchValue', 'Valeur')}</th>
                        <th style={{ width: '45%' }}>{t('carrierDetail.targetEmail', 'Email cible')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carrier.email_routing_rules.map((rule, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={idx}>
                          <td>{rule.match_field}</td>
                          <td>{rule.match_value}</td>
                          <td title={rule.email || rule.email_pattern}>{rule.email || rule.email_pattern}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </details>
        </section>
      )}
    </div>
  );

  const renderAddressInputs = (field, label, placeholder) => (
    <div className="u-field">
      <span className="u-label">{label}</span>
      {(carrierForm[field] || []).map((entry, index) => {
        const patterns = emailPatterns(entry);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="carrier-detail-v2__email-row">
            <input
              type="email"
              className="u-input"
              value={emailAddress(entry)}
              onChange={(e) => setEmailAt(field, index, e.target.value)}
              placeholder={placeholder}
              aria-label={`${label} ${index + 1}`}
            />
            {patterns.length > 0 && (
              <span className="u-tag" title={patterns.join(', ')}>
                {t('carriersUi.detail.addressPatterns', 'suivis {{patterns}}', { patterns: patterns.join(', ') })}
              </span>
            )}
            <button
              type="button"
              className="u-btn u-btn--ghost u-btn--icon"
              onClick={() => removeEmailAt(field, index)}
              aria-label={t('carriersUi.removeAddress', 'Retirer l’adresse')}
              title={t('carriersUi.removeAddress', 'Retirer l’adresse')}
            >
              <X aria-hidden="true" />
            </button>
          </div>
        );
      })}
      <div>
        <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => addEmail(field)}>
          <Plus aria-hidden="true" />
          {t('carriersUi.addAddress', 'Ajouter une adresse')}
        </button>
      </div>
    </div>
  );

  const renderContactEdit = () => (
    <Card
      icon={Pencil}
      title={t('carriersUi.detail.editTitle', 'Modifier {{name}}', { name: carrier.name })}
      description={t('carriersUi.detail.editDesc', 'Mode de contact, adresses et consignes de l’agent. Rien ne change avant « Enregistrer ».')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={() => { setIsEditingCarrier(false); setCarrierSaveError(null); }} disabled={carrierSaving}>
            {t('common.cancel', 'Annuler')}
          </button>
          <button type="button" className="u-btn u-btn--primary" onClick={handleSaveCarrier} disabled={carrierSaving}>
            {carrierSaving ? t('common.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        {carrierSaveError && <Banner tone="danger">{carrierSaveError}</Banner>}

        <div className="carrier-detail-v2__section-title u-overline">{t('carriersUi.detail.sectionIdentity', 'Identité')}</div>
        <Field label={t('carriersInstructions.name', 'Nom')} htmlFor="cd-name">
          <input id="cd-name" type="text" className="u-input" value={carrierForm.name} onChange={(e) => setCarrierForm((f) => ({ ...f, name: e.target.value }))} />
        </Field>
        <div className="carriers-v2__switches">
          <Switch
            id="cd-automation"
            checked={carrierForm.automation || false}
            onChange={(checked) => setCarrierForm((f) => ({ ...f, automation: checked }))}
            label={t('carriersList.automationEnabled', 'Automatisation activée pour ce transporteur')}
          />
          <Switch
            id="cd-is-mail"
            checked={carrierForm.is_mail || false}
            onChange={(checked) => setCarrierForm((f) => ({ ...f, is_mail: checked }))}
            label={t('carriersList.isMailLabel', 'Transporteur contacté par e-mail')}
          />
        </div>

        <div className="carrier-detail-v2__section-title u-overline">{t('carriersUi.detail.sectionContact', 'Contact')}</div>
        <div className="u-form-grid">
          {renderAddressInputs('emails', t('carriersUi.detail.addresses', 'Adresses e-mail'), t('carriersUi.add.emailPlaceholder', 'litiges@transporteur.fr'))}
          {renderAddressInputs('cc_emails', t('carriersUi.detail.cc', 'En copie'), t('carriersUi.detail.ccPlaceholder', 'transport@votre-societe.fr'))}
        </div>
        <Field
          label={t('carrierDetail.emailSubject', 'Objet des e-mails')}
          htmlFor="cd-email-subject"
          help={(
            <>
              {t('carrierDetail.emailSubjectHint', 'Variables : {{vars}}. Vide = format par défaut.', { vars: '{transporteur} {suivi} {objet} {commande}', interpolation: { escapeValue: false } })}
              <br />
              {t('carrierDetail.emailSubjectPreview', 'Aperçu')} : {previewCarrierSubject(carrierForm.email_subject_template, carrierForm.name || carrier.name)}
            </>
          )}
        >
          <input
            id="cd-email-subject"
            type="text"
            className="u-input"
            value={carrierForm.email_subject_template || ''}
            onChange={(e) => setCarrierForm((f) => ({ ...f, email_subject_template: e.target.value }))}
            maxLength={150}
            placeholder={DEFAULT_CARRIER_SUBJECT}
          />
        </Field>

        <div className="carrier-detail-v2__section-title u-overline">{t('carriersUi.detail.instructionsTitle', 'Consignes pour l’agent')}</div>
        <Field label={t('carriersInstructions.contactRules', 'Instructions de contact')} htmlFor="cd-contact-rules">
          <textarea id="cd-contact-rules" className="u-textarea" rows={5} value={carrierForm.contact_rules} onChange={(e) => setCarrierForm((f) => ({ ...f, contact_rules: e.target.value }))} />
        </Field>
        <Field
          label={t('carriersUi.detail.messageInstructions', 'Consignes de message')}
          htmlFor="cd-message-instructions"
          help={limit
            ? t('carriersUi.detail.alwaysTrackingLimit', 'Le numéro de suivi est toujours inclus, et les messages restent sous {{limit}} caractères, quelles que soient ces consignes.', { limit })
            : t('carriersUi.detail.alwaysTracking', 'Le numéro de suivi est toujours inclus dans le message, quelles que soient ces consignes.')}
        >
          <textarea
            id="cd-message-instructions"
            className="u-textarea"
            rows={4}
            maxLength={2000}
            value={carrierForm.message_instructions || ''}
            onChange={(e) => setCarrierForm((f) => ({ ...f, message_instructions: e.target.value }))}
            placeholder={t('carriersUi.detail.messageInstructionsPlaceholder', 'Consignes appliquées aux messages envoyés à ce transporteur (optionnel), en plus des consignes générales.')}
          />
        </Field>

        <details className="u-disclosure carrier-detail-v2__edit-advanced" open={advancedSet || undefined}>
          <summary>
            <ChevronRight aria-hidden="true" />
            {t('carriersUi.detail.advanced', 'Avancé : formulaire et routage des adresses')}
          </summary>
          <div className="u-form carrier-detail-v2__advanced-form">
            {showEnseignes && (
              <Field
                label={t('carrierDetail.defaultEnseigne', 'Enseigne Connect par défaut')}
                htmlFor="cd-default-enseigne"
                help={t('carrierDetail.defaultEnseigneHint', 'Essayée en dernier, après les alias du client et le nom de l’expéditeur, quand le portail Connect ne liste aucun d’eux.')}
              >
                <input
                  id="cd-default-enseigne"
                  type="text"
                  className="u-input"
                  value={carrierForm.default_enseigne || ''}
                  onChange={(e) => setCarrierForm((f) => ({ ...f, default_enseigne: e.target.value }))}
                  placeholder={t('carrierDetail.defaultEnseignePlaceholder', 'Enseigne utilisée quand le client n’a pas d’alias (ex. : votre propre enseigne)')}
                />
              </Field>
            )}
            <div className="u-form-grid">
              <Field
                label={t('carrierDetail.formTrackingPatterns', 'Suivis à traiter via formulaire (regex, une par ligne)')}
                htmlFor="cd-form-patterns"
                help={t('carrierDetail.formTrackingPatternsHint', 'Les numéros de suivi correspondant à ces motifs ne peuvent pas être traités par e-mail : l’IA est redirigée vers l’outil formulaire ci-dessous.')}
              >
                <textarea
                  id="cd-form-patterns"
                  className="u-textarea carrier-detail-v2__mono"
                  rows={3}
                  value={carrierForm.form_tracking_patterns || ''}
                  onChange={(e) => setCarrierForm((f) => ({ ...f, form_tracking_patterns: e.target.value }))}
                  placeholder="^JJD"
                />
              </Field>
              <Field label={t('carrierDetail.formTool', 'Outil formulaire dédié')} htmlFor="cd-form-tool">
                <input
                  id="cd-form-tool"
                  type="text"
                  className="u-input"
                  value={carrierForm.form_tool || ''}
                  onChange={(e) => setCarrierForm((f) => ({ ...f, form_tool: e.target.value }))}
                  placeholder={t('carrierDetail.formToolPlaceholder', 'Ex. contact_dhl, contact_gls, create_fedex_ticket')}
                />
              </Field>
            </div>
            <Field
              label={t('carrierDetail.web2caseUrl', 'URL du formulaire web transporteur (Web2Case)')}
              htmlFor="cd-web2case"
              help={t('carrierDetail.web2caseUrlHint', 'URL (avec la clé de compte) fournie par le transporteur pour ouvrir les demandes via son formulaire web. Utilisée par l’outil formulaire dédié.')}
            >
              <input
                id="cd-web2case"
                type="text"
                className="u-input"
                value={carrierForm.web2case_url || ''}
                onChange={(e) => setCarrierForm((f) => ({ ...f, web2case_url: e.target.value }))}
                placeholder="https://reach-cbs.my.site.com/Web2Case/?cp=...&Key=..."
              />
            </Field>
            <div className="u-field">
              <span className="u-label">{t('carrierDetail.formDefaults', 'Valeurs par défaut du formulaire')}</span>
              {(carrierForm.form_defaults || []).map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="carrier-detail-v2__kv-row">
                  <input
                    type="text"
                    className="u-input carrier-detail-v2__mono"
                    value={row.key}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, form_defaults: f.form_defaults.map((r, i) => (i === index ? { ...r, key: e.target.value } : r)) }))}
                    placeholder={t('carrierDetail.formDefaultsKey', 'Clé (ex. charger_number)')}
                    aria-label={t('carriersUi.detail.formDefaultKey', 'Clé')}
                  />
                  <input
                    type="text"
                    className="u-input"
                    value={row.value}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, form_defaults: f.form_defaults.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)) }))}
                    placeholder={t('carrierDetail.formDefaultsValue', 'Valeur')}
                    aria-label={t('carrierDetail.formDefaultsValue', 'Valeur')}
                  />
                  <button
                    type="button"
                    className="u-btn u-btn--ghost u-btn--icon"
                    onClick={() => setCarrierForm((f) => ({ ...f, form_defaults: f.form_defaults.filter((_, i) => i !== index) }))}
                    aria-label={t('carriersUi.remove', 'Retirer')}
                    title={t('carriersUi.remove', 'Retirer')}
                  >
                    <X aria-hidden="true" />
                  </button>
                </div>
              ))}
              <div>
                <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setCarrierForm((f) => ({ ...f, form_defaults: [...(f.form_defaults || []), { key: '', value: '' }] }))}>
                  <Plus aria-hidden="true" />
                  {t('carrierDetail.formDefaultsAdd', 'Ajouter une valeur')}
                </button>
              </div>
              <p className="u-help">{t('carrierDetail.formDefaultsHint', 'Données fixes de votre compte pré-remplies dans le formulaire du transporteur, jamais devinées par l’IA. GLS (contact_gls) : charger_number (n° chargeur), shipping_department (code agence), et le contact via « Identité de réclamation » ou les clés email, first_name, last_name, phone, company_name. DHL (contact_dhl) : email du demandeur (sinon le contact de réclamation, sinon l’e-mail du compte).')}</p>
            </div>
            <Field label={`${t('carrierDetail.emailRoutingRules', 'Règles de routage')} (${t('carrierDetail.advancedJson', 'JSON')})`} htmlFor="cd-routing">
              <textarea
                id="cd-routing"
                className="u-textarea carrier-detail-v2__mono"
                rows={6}
                value={carrierForm.email_routing_rules || '[]'}
                onChange={(e) => setCarrierForm((f) => ({ ...f, email_routing_rules: e.target.value }))}
                placeholder={'[\n  { "match_field": "sender_postcode_prefix", "match_value": "06", "email": "suivi@carrier.fr" }\n]'}
              />
            </Field>
          </div>
        </details>
      </div>
    </Card>
  );

  const renderAccounts = () => (
    <Card
      icon={KeyRound}
      title={t('carriersUi.portal.title', 'Comptes portail')}
      badge={needsAccount ? <Badge tone="accent">{t('carriersUi.portal.required', 'Exigé par le portail')}</Badge> : null}
      description={t('carriersUi.portal.desc', 'Les identifiants que l’agent utilise pour se connecter au portail {{name}}. Un mot de passe enregistré n’est jamais réaffiché.', { name: carrier.name })}
      actions={(
        <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={openAddAccount}>
          <Plus aria-hidden="true" />
          {t('carriersUi.portal.add', 'Ajouter un compte')}
        </button>
      )}
      flush
    >
      {credentialsLoading ? (
        <div className="carriers-v2__skeleton" aria-busy="true">
          <span className="u-skeleton" />
          <span className="u-skeleton" />
        </div>
      ) : credentials.length === 0 ? (
        <EmptyState icon={KeyRound} title={t('carriersUi.portal.empty', 'Aucun compte portail')}>
          {needsAccount
            ? t('carriersUi.attention.missingAccountHint', 'L’agent ne peut pas déposer de demande sur son portail sans identifiants.')
            : t('carriersUi.portal.emptyOptional', 'Ajoutez un compte si l’agent doit se connecter au portail de ce transporteur.')}
        </EmptyState>
      ) : (
        <ul className="u-list">
          {credentials.map((cred) => (
            <li className="u-row" key={cred.id}>
              <IconChip icon={KeyRound} tone={cred.is_valid === false ? 'danger' : 'success'} size="sm" />
              <div className="u-row__main">
                <div className="u-row__title">
                  {cred.label || cred.username}
                  {cred.is_valid === false
                    ? <Badge tone="danger" dot>{t('carriersUi.portal.refused', 'Refusé par le portail')}</Badge>
                    : <Badge tone="success" dot>{t('carrierDetail.valid', 'Valide')}</Badge>}
                  {cred.scope?.countries?.length > 0 && <Badge icon={Globe}>{scopeLabel(cred.scope)}</Badge>}
                </div>
                <div className="u-row__meta">
                  {[
                    cred.username,
                    t('carriersUi.portal.password', 'mot de passe enregistré'),
                    cred.last_used && t('carriersUi.portal.lastUsed', 'dernier usage le {{date}}', { date: formatDateTime(cred.last_used) }),
                  ].filter(Boolean).join(' · ')}
                </div>
                <div className="carrier-detail-v2__clients">
                  <Users aria-hidden="true" />
                  {cred.client_ids?.length > 0
                    ? cred.client_ids.map((cid) => <span key={cid} className="u-tag">{getClientName(cid)}</span>)
                    : <span className="u-muted">{t('carrierDetail.defaultAccount', 'Par défaut (tous les clients)')}</span>}
                </div>
              </div>
              <div className="u-row__side">
                <OverflowMenu
                  label={t('carriersUi.actions', 'Actions')}
                  items={[
                    { label: t('carrierDetail.editCredential', 'Modifier le compte'), icon: Pencil, onClick: () => openEditAccount(cred) },
                    { separator: true },
                    { label: t('carriersUi.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => askDeleteAccount(cred) },
                  ]}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );

  const renderOtp = () => (
    <Card
      icon={ShieldCheck}
      title={t('carriersUi.otp.title', 'Boîte des codes de connexion')}
      badge={otpMailbox?.configured
        ? <Badge tone="success" dot>{t('carriersUi.otp.connected', 'Connectée')}</Badge>
        : (needsOtp ? <Badge tone="warning" dot>{t('carriersUi.otp.required', 'Exigée par le portail')}</Badge> : null)}
      description={t('carrierDetail.otpMailboxHelp', 'Si le portail de ce transporteur exige un code de vérification envoyé par email à la connexion (double authentification), connectez ici la boîte qui reçoit ces codes. Elle est utilisée uniquement pour lire les codes de connexion — jamais pour les tickets.')}
      flush
    >
      {otpError && <div className="carrier-detail-v2__pad"><Banner tone="danger">{otpError}</Banner></div>}
      {otpMailboxLoading ? (
        <div className="carriers-v2__skeleton" aria-busy="true"><span className="u-skeleton" /></div>
      ) : otpMailbox?.configured ? (
        <ul className="u-list">
          <li className="u-row">
            <span className="u-logo carrier-detail-v2__provider" aria-hidden="true">
              {isOutlook(otpMailbox.provider) ? <MicrosoftMark /> : <SiGmail color="#EA4335" />}
            </span>
            <div className="u-row__main">
              <div className="u-row__title">
                {otpMailbox.email}
                <Badge>{otpProviderLabel(otpMailbox.provider)}</Badge>
              </div>
              <div className="u-row__meta">
                {t('carriersUi.otp.connectedAt', 'Connectée le {{date}} · lue uniquement pour les codes du portail', { date: formatDateTime(otpMailbox.connected_at) })}
              </div>
            </div>
            <div className="u-row__side">
              <OverflowMenu
                label={t('carriersUi.actions', 'Actions')}
                items={[{ label: t('carriersUi.otp.disconnect', 'Déconnecter'), icon: Unplug, danger: true, onClick: askDisconnectOtp }]}
              />
            </div>
          </li>
        </ul>
      ) : (
        <ul className="u-list">
          {otpLinkableAccounts.map((account) => {
            const key = `${account.provider}:${account.email}`;
            return (
              <li className="u-row" key={key}>
                <span className="u-logo carrier-detail-v2__provider" aria-hidden="true">
                  {isOutlook(account.provider) ? <MicrosoftMark /> : <SiGmail color="#EA4335" />}
                </span>
                <div className="u-row__main">
                  <div className="u-row__title">{account.email}</div>
                  <div className="u-row__meta">{t('carriersUi.otp.linkableHint', 'Déjà connectée à Unanimity : aucune nouvelle connexion nécessaire.')}</div>
                </div>
                <div className="u-row__side">
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => handleLinkOtpAccount(account)} disabled={otpLinking !== null}>
                    {otpLinking === key ? t('common.loading', 'Chargement…') : t('carriersUi.otp.use', 'Utiliser')}
                  </button>
                </div>
              </li>
            );
          })}
          <li className="u-row">
            <span className="u-logo carrier-detail-v2__provider" aria-hidden="true"><MicrosoftMark /></span>
            <div className="u-row__main">
              <div className="u-row__title">{t('carriersUi.otp.outlook', 'Compte Outlook')}</div>
              <div className="u-row__meta">{t('carriersUi.otp.outlookHint', 'Connexion Microsoft : vous serez redirigé puis ramené ici.')}</div>
            </div>
            <div className="u-row__side">
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleConnectOtpOutlook} disabled={otpOutlookLoading}>
                {otpOutlookLoading ? t('common.loading', 'Chargement…') : t('carriersUi.otp.connect', 'Connecter')}
              </button>
            </div>
          </li>
          <li className="u-row">
            <span className="u-logo carrier-detail-v2__provider" aria-hidden="true"><SiGmail color="#EA4335" /></span>
            <div className="u-row__main">
              <div className="u-row__title">{t('carriersUi.otp.gmail', 'Boîte Gmail (mot de passe d’application)')}</div>
              <div className="u-row__meta">{t('carriersUi.otp.gmailHint', 'Ou toute boîte IMAP : Unanimity vérifie la connexion avant d’enregistrer.')}</div>
            </div>
            <div className="u-row__side">
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => { setOtpForm(emptyOtpForm); setOtpError(null); setShowOtpPassword(false); }}>
                {t('carriersUi.otp.connect', 'Connecter')}
              </button>
            </div>
          </li>
        </ul>
      )}
    </Card>
  );

  const renderClaim = () => {
    const contact = carrier.claim_contact;
    const bank = carrier.claim_bank;
    if (showClaimForm) {
      const field = (key, label, required, type = 'text') => (
        <Field key={key} label={`${label}${required ? ' *' : ''}`} htmlFor={`claim-${key}`}>
          <input id={`claim-${key}`} type={type} className="u-input" value={claimForm[key]} onChange={(e) => setClaimForm({ ...claimForm, [key]: e.target.value })} />
        </Field>
      );
      return (
        <Card
          icon={Landmark}
          title={t('carrierDetail.claimIdentityTitle', 'Identité de réclamation')}
          description={t('carrierDetail.claimIdentityHelp', 'Contact et coordonnées bancaires utilisés pour remplir les formulaires de réclamation de ce transporteur (remboursements). L’IBAN est chiffré et n’est jamais réaffiché en clair.')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setShowClaimForm(false)} disabled={claimSaving}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={handleSaveClaim} disabled={claimSaving}>
                {claimSaving ? t('common.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
              </button>
            </>
          )}
        >
          <div className="u-form">
            {claimError && <Banner tone="danger">{claimError}</Banner>}
            <div className="carrier-detail-v2__section-title u-overline">{t('carrierDetail.claimContactLabel', 'Contact')}</div>
            <div className="u-form-grid">
              <Field label={t('carrierDetail.claimContactAs', 'Déclarant')} htmlFor="claim-contact-as">
                <select id="claim-contact-as" className="u-select" value={claimForm.contact_as} onChange={(e) => setClaimForm({ ...claimForm, contact_as: e.target.value })}>
                  <option value="sender">{t('carrierDetail.claimContactAsSender', 'Expéditeur')}</option>
                  <option value="recipient">{t('carrierDetail.claimContactAsRecipient', 'Destinataire')}</option>
                  <option value="other">{t('carrierDetail.claimContactAsOther', 'Autre')}</option>
                </select>
              </Field>
              {field('company_name', t('carrierDetail.claimCompany', 'Société'), false)}
              {field('first_name', t('carrierDetail.claimFirstName', 'Prénom'), true)}
              {field('last_name', t('carrierDetail.claimLastName', 'Nom'), true)}
              {field('email', t('carrierDetail.claimEmail', 'Email'), true, 'email')}
              {field('phone_number', t('carrierDetail.claimPhone', 'Téléphone'), true, 'tel')}
              {field('account_number', t('carrierDetail.claimAccountNumber', 'N° de compte transporteur'), false)}
              {field('address_line1', t('carrierDetail.claimAddress1', 'Adresse'), false)}
              {field('postal_code', t('carrierDetail.claimPostalCode', 'Code postal'), false)}
              {field('city', t('carrierDetail.claimCity', 'Ville'), false)}
            </div>
            <div className="carrier-detail-v2__section-title u-overline">{t('carrierDetail.claimBankLabel', 'Banque')}</div>
            <div className="u-form-grid">
              <Field
                label={`${t('carrierDetail.claimIban', 'IBAN')} *`}
                htmlFor="claim-iban"
                help={bank?.configured
                  ? t('carriersUi.claim.ibanSaved', 'IBAN enregistré (•••• {{last4}}) : laissez vide pour le garder, ressaisissez-le en entier pour modifier la banque.', { last4: bank.iban_last4 })
                  : t('carriersUi.claim.ibanHelp', 'Chiffré à l’enregistrement, jamais réaffiché.')}
              >
                <input id="claim-iban" type="text" className="u-input" autoComplete="off" value={claimForm.iban} onChange={(e) => setClaimForm({ ...claimForm, iban: e.target.value })} placeholder={bank?.configured ? `•••• ${bank.iban_last4}` : 'FR76 …'} />
              </Field>
              {field('bic', t('carrierDetail.claimBic', 'BIC'), true)}
              {field('bank_name', t('carrierDetail.claimBankName', 'Banque'), true)}
              {field('account_holder_name', t('carrierDetail.claimHolder', 'Titulaire du compte'), true)}
            </div>
          </div>
        </Card>
      );
    }
    return (
      <Card
        icon={Landmark}
        title={t('carrierDetail.claimIdentityTitle', 'Identité de réclamation')}
        badge={contact
          ? <Badge tone="success" dot>{t('carriersUi.claim.set', 'Renseignée')}</Badge>
          : <Badge tone="warning" dot>{t('carriersUi.claim.missing', 'À remplir')}</Badge>}
        description={t('carrierDetail.claimIdentityHelp', 'Contact et coordonnées bancaires utilisés pour remplir les formulaires de réclamation de ce transporteur (remboursements). L’IBAN est chiffré et n’est jamais réaffiché en clair.')}
        actions={contact ? (
          <OverflowMenu
            label={t('carriersUi.actions', 'Actions')}
            items={[{ label: t('carrierDetail.claimClear', 'Supprimer'), icon: Trash2, danger: true, onClick: askClearClaim }]}
          />
        ) : null}
        footer={(
          <button type="button" className="u-btn u-btn--primary" onClick={openClaimForm}>
            {contact ? t('common.edit', 'Modifier') : t('carrierDetail.claimConfigure', 'Configurer l’identité de réclamation')}
          </button>
        )}
      >
        {contact ? (
          <DescriptionList items={[
            { label: t('carrierDetail.claimContactLabel', 'Contact'), value: [[contact.first_name, contact.last_name].filter(Boolean).join(' '), contact.email, contact.phone_number].filter(Boolean).join(' · ') },
            { label: t('carrierDetail.claimCompany', 'Société'), value: contact.company_name },
            { label: t('carrierDetail.claimAccountLabel', 'N° de compte transporteur'), value: contact.account_number },
            { label: t('carrierDetail.claimAddress1', 'Adresse'), value: [contact.address_line1, [contact.postal_code, contact.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') },
            {
              label: t('carrierDetail.claimBankLabel', 'Banque'),
              value: bank?.configured
                ? [bank.bank_name, bank.bic, `IBAN •••• ${bank.iban_last4}`, bank.account_holder_name].filter(Boolean).join(' · ')
                : <Badge tone="warning" dot>{t('carrierDetail.claimBankMissing', 'Non configurée')}</Badge>,
            },
          ]}
          />
        ) : (
          <EmptyState icon={Landmark} title={t('carriersUi.claim.emptyTitle', 'Aucune identité de réclamation')}>
            {t('carriersUi.detail.attClaimHint', 'Le formulaire de réclamation de ce transporteur demande un contact et des coordonnées bancaires.')}
          </EmptyState>
        )}
      </Card>
    );
  };

  const renderEnseignes = () => {
    const data = enseigneSync.data;
    const chosen = Object.values(enseigneChoices).filter(Boolean).length;
    return (
      <Card
        icon={Store}
        title={t('carrierDetail.enseignesTitle', 'Enseignes Connect')}
        description={(
          <>
            {t('carrierDetail.enseignesHint', 'Lit les enseignes des comptes Connect configurés et propose à quel client chacune correspond. Une réclamation Mondial Relay exige l’enseigne exacte du portail : confirmez les correspondances pour qu’elles soient enregistrées comme alias des clients.')}
            {carrier.enseignes_synced_at && (
              <> {t('carrierDetail.enseignesSyncedAt', 'Dernière synchronisation : {{date}}', { date: formatDateTime(carrier.enseignes_synced_at) })}</>
            )}
          </>
        )}
        actions={(
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={syncEnseignes} disabled={enseigneSync.loading}>
            <RefreshCw aria-hidden="true" />
            {enseigneSync.loading ? t('carrierDetail.enseignesSyncing', 'Synchronisation…') : t('carrierDetail.enseignesSync', 'Synchroniser les enseignes')}
          </button>
        )}
        footer={data ? (
          <button type="button" className="u-btn u-btn--primary" onClick={applyEnseignes} disabled={enseigneSync.loading || chosen === 0}>
            {t('carrierDetail.enseignesApply', 'Enregistrer les correspondances')}
          </button>
        ) : null}
      >
        <div className="u-form">
          {enseigneSync.error && <Banner tone="danger">{enseigneSync.error}</Banner>}
          {enseigneSync.applied && (
            <Banner tone="success">
              {tp('carriersUi.enseignes.applied', '{{count}} correspondance enregistrée.', '{{count}} correspondances enregistrées.', { count: (enseigneSync.applied.applied || []).length })}
            </Banner>
          )}
          {!data && (carrier.enseignes || []).length > 0 && (
            <div className="carrier-detail-v2__codes">
              <span className="u-overline">{t('carriersUi.enseignes.known', 'Enseignes connues')}</span>
              {carrier.enseignes.map((name) => <span key={name} className="u-tag">{name}</span>)}
            </div>
          )}
          {!data && (carrier.enseignes || []).length === 0 && (
            <p className="u-muted carrier-detail-v2__text">{t('carriersUi.enseignes.never', 'Aucune synchronisation pour l’instant : lancez-la pour lire les enseignes des comptes Connect.')}</p>
          )}
          {data && (
            <>
              <div className="carrier-detail-v2__codes">
                {(data.accounts || []).map((account) => (
                  <Badge key={account.label} tone={account.error ? 'danger' : 'success'} dot>
                    {account.label} : {account.error
                      ? account.error
                      : tp('carriersUi.enseignes.count', '{{count}} enseigne', '{{count}} enseignes', { count: account.count })}
                  </Badge>
                ))}
              </div>
              {(data.enseignes || []).length === 0 ? (
                <p className="u-muted carrier-detail-v2__text">{t('carrierDetail.enseignesNone', 'Aucune enseigne lue sur les comptes configurés.')}</p>
              ) : (
                <div className="u-table-scroll">
                  <table className="u-table carrier-detail-v2__enseignes">
                    <thead>
                      <tr>
                        <th style={{ width: '45%' }}>{t('carrierDetail.enseignesColEnseigne', 'Enseigne Connect')}</th>
                        <th style={{ width: '55%' }}>{t('carrierDetail.enseignesColClient', 'Client')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.enseignes.map((row) => (
                        <tr key={row.enseigne}>
                          <td title={row.enseigne}>{row.enseigne}</td>
                          <td>
                            {row.status === 'mapped' ? (
                              <Badge tone="success" icon={CircleCheck}>{row.client_name}</Badge>
                            ) : (
                              <select
                                className="u-select"
                                value={enseigneChoices[row.enseigne] || ''}
                                onChange={(e) => setEnseigneChoices((c) => ({ ...c, [row.enseigne]: e.target.value }))}
                                aria-label={t('carriersUi.enseignes.clientFor', 'Client de l’enseigne {{name}}', { name: row.enseigne })}
                              >
                                <option value="">
                                  {row.proposal
                                    ? t('carrierDetail.enseignesProposalHint', 'Proposition : {{name}}', { name: row.proposal.client_name })
                                    : t('carrierDetail.enseignesUnmapped', 'Aucune correspondance proposée')}
                                </option>
                                {ednClients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
                              </select>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(data.clients_without_enseigne || []).length > 0 && (
                <p className="u-help">
                  {t('carrierDetail.enseignesClientsWithout', 'Clients sans enseigne sur ces comptes : {{names}}', {
                    names: data.clients_without_enseigne.map((c) => c.name).join(', '),
                  })}
                </p>
              )}
            </>
          )}
        </div>
      </Card>
    );
  };

  const renderAccountModal = () => (
    <Modal
      icon={KeyRound}
      title={accountModal.id ? t('carrierDetail.editCredential', 'Modifier le compte') : t('carriersUi.portal.addTitle', 'Ajouter un compte portail {{name}}', { name: carrier.name })}
      description={t('carriersUi.portal.modalDesc', 'Unanimity chiffre le mot de passe et ne le réaffiche jamais.')}
      onClose={() => setAccountModal(null)}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={() => setAccountModal(null)} disabled={accountSaving}>
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            type="button"
            className="u-btn u-btn--primary"
            onClick={handleSaveAccount}
            disabled={accountSaving || !accountForm.username || (!accountModal.id && !accountForm.password)}
          >
            {accountSaving ? t('common.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        {accountError && <Banner tone="danger">{accountError}</Banner>}
        <Field label={t('carrierDetail.label', 'Libellé')} htmlFor="account-label">
          <input id="account-label" type="text" className="u-input" value={accountForm.label} onChange={(e) => setAccountForm((f) => ({ ...f, label: e.target.value }))} placeholder={t('carriersUi.portal.labelPlaceholder', 'Ex. Principal, Client X')} />
        </Field>
        <div className="u-form-grid">
          <Field label={t('carriersUi.username', 'Identifiant')} htmlFor="account-username">
            <input id="account-username" type="text" className="u-input" autoComplete="off" value={accountForm.username} onChange={(e) => setAccountForm((f) => ({ ...f, username: e.target.value }))} />
          </Field>
          <Field
            label={t('carriersUi.password', 'Mot de passe')}
            htmlFor="account-password"
            help={accountModal.id ? t('carriersUi.portal.passwordKeep', 'Enregistré — laissez vide pour le garder.') : undefined}
          >
            <input
              id="account-password"
              type="password"
              className="u-input"
              autoComplete="new-password"
              value={accountForm.password}
              onChange={(e) => setAccountForm((f) => ({ ...f, password: e.target.value }))}
              placeholder={accountModal.id ? '••••••••' : ''}
            />
          </Field>
        </div>
        <div className="u-field">
          <span className="u-label">{t('carrierDetail.mappedClients', 'Clients associés')}</span>
          <div className="carrier-detail-v2__chips" role="group" aria-label={t('carrierDetail.mappedClients', 'Clients associés')}>
            {ednClients.map((client) => {
              const selected = accountForm.client_ids.includes(client.id);
              return (
                <button
                  key={client.id}
                  type="button"
                  className={`carrier-detail-v2__chip${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => toggleClient(client.id)}
                >
                  {client.name}
                  {selected && <CircleCheck aria-hidden="true" />}
                </button>
              );
            })}
            {ednClients.length === 0 && <span className="u-muted">{t('carrierDetail.noClientsAvailable', 'Aucun client disponible')}</span>}
          </div>
          <p className="u-help">{t('carriersUi.portal.clientsHelp', 'Aucun client choisi = compte par défaut, pour tous les clients.')}</p>
        </div>
        <Field label={t('carrierDetail.destinationScope', 'Périmètre de destination')} htmlFor="account-scope">
          <select id="account-scope" className="u-select" value={accountForm.scope_preset || 'any'} onChange={(e) => setAccountForm((f) => ({ ...f, scope_preset: e.target.value }))}>
            <option value="any">{t('carrierDetail.scopeAny', 'Toutes destinations (défaut)')}</option>
            <option value="fr">{t('carrierDetail.scopeFrance', 'France uniquement')}</option>
            <option value="intl">{t('carrierDetail.scopeInternational', 'International (hors France)')}</option>
            <option value="custom">{t('carrierDetail.scopeCustom', 'Liste de pays personnalisée...')}</option>
          </select>
        </Field>
        {accountForm.scope_preset === 'custom' && (
          <>
            <Field label={t('carriersUi.portal.countries', 'Pays')} htmlFor="account-countries">
              <input id="account-countries" type="text" className="u-input" value={accountForm.scope_countries} onChange={(e) => setAccountForm((f) => ({ ...f, scope_countries: e.target.value }))} placeholder={t('carrierDetail.scopeCountriesPlaceholder', 'Codes pays, ex. FR, BE, LU')} />
            </Field>
            <Switch
              id="account-scope-invert"
              checked={!!accountForm.scope_invert}
              onChange={(checked) => setAccountForm((f) => ({ ...f, scope_invert: checked }))}
              label={t('carrierDetail.scopeInvert', 'Toutes les destinations SAUF ces pays')}
            />
          </>
        )}
      </div>
    </Modal>
  );

  const renderOtpModal = () => (
    <Modal
      icon={Mail}
      title={t('carrierDetail.otpImapTitle', 'Connecter une boîte Gmail (mot de passe d’application)')}
      description={t('carriersUi.otp.modalDesc', 'La boîte qui reçoit les codes de connexion du portail {{name}}.', { name: carrier.name })}
      onClose={() => setOtpForm(null)}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={() => { setOtpForm(null); setOtpError(null); }} disabled={otpSaving}>
            {t('common.cancel', 'Annuler')}
          </button>
          <button type="button" className="u-btn u-btn--primary" onClick={handleSaveOtpImap} disabled={otpSaving || !otpForm.email || !otpForm.password}>
            {otpSaving ? t('carrierDetail.otpVerifying', 'Vérification...') : t('carriersUi.otp.verifySave', 'Vérifier et enregistrer')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        {otpError && <Banner tone="danger">{otpError}</Banner>}
        <div className="u-form-grid">
          <Field label={t('carrierDetail.otpImapEmail', 'Adresse email')} htmlFor="otp-email">
            <input id="otp-email" type="email" className="u-input" value={otpForm.email} onChange={(e) => setOtpForm((f) => ({ ...f, email: e.target.value }))} />
          </Field>
          <Field label={t('carrierDetail.otpImapPassword', 'Mot de passe d’application')} htmlFor="otp-password">
            <div className="u-input-group">
              <input
                id="otp-password"
                type={showOtpPassword ? 'text' : 'password'}
                className="u-input"
                autoComplete="new-password"
                value={otpForm.password}
                onChange={(e) => setOtpForm((f) => ({ ...f, password: e.target.value }))}
              />
              <button
                type="button"
                className="u-input-group__btn"
                onClick={() => setShowOtpPassword((v) => !v)}
                aria-label={showOtpPassword ? t('carriersUi.hide', 'Masquer') : t('carriersUi.show', 'Afficher')}
                title={showOtpPassword ? t('carriersUi.hide', 'Masquer') : t('carriersUi.show', 'Afficher')}
              >
                {showOtpPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
          </Field>
          <Field label={t('carrierDetail.otpImapServer', 'Serveur IMAP')} htmlFor="otp-server">
            <input id="otp-server" type="text" className="u-input" value={otpForm.imap_server} onChange={(e) => setOtpForm((f) => ({ ...f, imap_server: e.target.value }))} />
          </Field>
          <Field label={t('carrierDetail.otpImapPort', 'Port IMAP')} htmlFor="otp-port">
            <input id="otp-port" type="number" className="u-input" value={otpForm.imap_port} onChange={(e) => setOtpForm((f) => ({ ...f, imap_port: Number(e.target.value) || 993 }))} />
          </Field>
        </div>
        <p className="u-help">
          {t('carrierDetail.otpImapHelp', 'Pour Gmail, créez un mot de passe d’application (Compte Google > Sécurité > Mots de passe des applications) — pas votre mot de passe habituel.')}{' '}
          <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noreferrer">{t('carrierDetail.otpImapHelpLink', 'Guide Google')}</a>
        </p>
      </div>
    </Modal>
  );

  const renderTabPanel = () => {
    if (tab === 'portal') return <div className="carriers-v2__stack">{renderAccounts()}{renderOtp()}</div>;
    if (tab === 'claim') return renderClaim();
    if (tab === 'enseignes') return renderEnseignes();
    if (tab === 'recipes') return <CarrierRecipes carrierName={carrier?.name} />;
    if (tab === 'delays') return <CarrierAnswerDelays data={answerDelays} carrierName={carrier?.name} />;
    return isEditingCarrier ? renderContactEdit() : renderContactView();
  };

  const tabDefs = {
    contact: { key: 'contact', label: t('carriersUi.detail.tabContact', 'Contact et consignes'), icon: Mail },
    portal: { key: 'portal', label: t('carriersUi.detail.tabPortal', 'Accès au portail'), icon: KeyRound, count: credentialsLoading ? undefined : credentials.length },
    claim: { key: 'claim', label: t('carriersUi.detail.tabClaim', 'Identité de réclamation'), icon: Landmark },
    enseignes: { key: 'enseignes', label: t('carriersUi.detail.tabEnseignes', 'Enseignes'), icon: Store },
    recipes: { key: 'recipes', label: t('carriersUi.detail.tabRecipes', 'Recettes de portail'), icon: FlaskConical },
    delays: { key: 'delays', label: t('carrierDelays.tab', 'Délais de réponse'), icon: Timer },
  };

  return (
    <div className="u-page carrier-detail-v2">
      {renderHeader()}
      <div className="u-well">
        {carrierLoading && !carrier ? (
          <>
            <div className="u-kpi-row" aria-hidden="true">
              {[0, 1, 2].map((i) => <span key={i} className="u-skeleton carriers-v2__kpi-skeleton" />)}
            </div>
            <span className="u-skeleton carrier-detail-v2__card-skeleton" aria-hidden="true" />
          </>
        ) : carrierError ? (
          <section className="u-card">
            <EmptyState
              icon={Truck}
              title={carrierError}
              action={(
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => navigate('/carriers-instructions')}>
                  <ChevronLeft aria-hidden="true" />
                  {t('carrierDetail.backToCarriers', 'Retour aux transporteurs')}
                </button>
              )}
            />
          </section>
        ) : carrier && (
          <>
            {renderAttention()}
            {renderKpis()}
            {/* One view left (e-mail-only carrier without recipes): no tab bar. */}
            {visibleTabs.length > 1 ? (
              <>
                <Tabs
                  label={carrier.name}
                  active={tab}
                  onChange={(next) => setTab(next)}
                  tabs={visibleTabs.map((key) => tabDefs[key])}
                />
                <div role="tabpanel">{renderTabPanel()}</div>
              </>
            ) : renderTabPanel()}
          </>
        )}
      </div>

      {accountModal && carrier && renderAccountModal()}
      {otpForm && carrier && renderOtpModal()}
      {confirmation && (
        <ConfirmDialog
          title={confirmation.title}
          message={confirmation.message}
          confirmLabel={confirmation.confirmLabel}
          cancelLabel={t('common.cancel', 'Annuler')}
          onConfirm={runConfirmation}
          onCancel={() => setConfirmation(null)}
          busy={confirming}
        />
      )}
      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

const MODE_ICON = { portal: Globe, form: FileText, api: Globe, email: Mail, email_portal: Mail };

// Third-party marks on their white tile, in their own colour (kit: logos).
const MicrosoftMark = () => <SiMicrosoftoutlook color="#0078D4" />;

export default CarrierDetail;
